const BASE_URL = import.meta.env.DEV
  ? 'http://64.23.188.15:8000/api/v1'
  : '/api/v1';

const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('token');
  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && token !== 'undefined'
        ? { Authorization: `Bearer ${token.replace(/"/g, '')}` }
        : {}),
    },
    cache: 'no-store',
    credentials: 'omit',
    mode: 'cors',
  };
  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: { ...defaultOptions.headers, ...(options.headers || {}) },
  };
  const response = await fetch(url, mergedOptions);
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// ==================== INTERFACES ====================

export interface RoomType {
  _id: string;
  name: string;
  description?: string;
  hourlyRate: number;
  nightRate: number;
  fullDayRate: number;
  capacity: number;
  amenities: string[];
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

export interface Room {
  _id: string;
  roomNumber: string;
  roomTypeId: RoomType | string;
  status: 'available' | 'occupied' | 'reserved' | 'maintenance';
  capacity: number;
  description?: string;
  floor?: number;
  customHourlyRate?: number;
  customNightRate?: number;
  customFullDayRate?: number;
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

export function getEffectiveRates(room: Room) {
  const rt = typeof room.roomTypeId === 'object' ? room.roomTypeId : null;
  return {
    hourlyRate: room.customHourlyRate ?? rt?.hourlyRate ?? 0,
    nightRate: room.customNightRate ?? rt?.nightRate ?? 0,
    fullDayRate: room.customFullDayRate ?? rt?.fullDayRate ?? 0,
  };
}

export interface Booking {
  _id: string;
  roomId: Room | string;
  clientName: string;
  clientPhone?: string;
  clientEmail?: string;
  cardNumber?: string;
  nomFemme?: string;
  checkIn: string;
  checkOut?: string;
  status: 'pending' | 'checked-in' | 'checked-out' | 'cancelled';
  rateType: 'standard' | 'negotiated';
  negotiatedRate?: number;
  calculatedAmount?: number;
  finalAmount?: number;
  paidAmount?: number;
  remainingAmount?: number;
  paymentMethod?: 'cash' | 'mobile_money' | 'bank_transfer' | 'card';
  paymentDate?: string;
  notes?: string;
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBookingDto {
  roomId: string;
  clientName: string;
  clientPhone?: string;
  clientEmail?: string;
  cardNumber?: string;
  nomFemme?: string;
  checkIn: string;
  checkOut?: string;
  rateType: 'standard' | 'negotiated';
  negotiatedRate?: number;
  paidAmount?: number;
  paymentMethod?: 'cash' | 'mobile_money' | 'bank_transfer' | 'card';
  notes?: string;
}

export interface HotelInvoice {
  _id: string;
  bookingId: Booking | string;
  invoiceNumber: string;
  clientName: string;
  clientPhone: string;
  roomNumber: string;
  roomType: string;
  checkIn: string;
  checkOut: string;
  duration: { days: number; hours: number };
  rateApplied: string;
  rateDetails: string;
  totalAmount: number;
  cardNumber: string;
  status: 'draft' | 'issued' | 'paid' | 'cancelled';
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

// ==================== REPORT INTERFACES ====================

export interface ReportSummary {
  totalBookings: number;
  totalFinalAmount: number;
  totalPaidAmount: number;
  totalRemainingAmount: number;
  averageAmount: number;
  bookingsByStatus: Array<{ _id: string; count: number; totalAmount: number; totalPaid: number }>;
  bookingsByDay: Array<{ _id: string; count: number; totalAmount: number; totalPaid: number }>;
  bookingsByRateType: Array<{ _id: string; count: number; totalAmount: number }>;
  bookingsByPaymentMethod: Array<{ _id: string; count: number; totalPaid: number }>;
}

export interface ReportDetailedBooking {
  _id: string;
  roomId?: { _id: string; roomNumber: string; roomTypeId?: { _id: string; name: string } };
  clientName: string;
  clientPhone?: string;
  nomFemme?: string;
  checkIn: string;
  checkOut?: string;
  durationText?: string;
  status: string;
  rateType: string;
  finalAmount: number;
  calculatedAmount?: number;
  paidAmount: number;
  remainingAmount: number;
  paymentMethod: string;
  paymentStatus: 'paid' | 'partial' | 'unpaid' | 'no-amount';
  createdAt: string;
}

export interface ReportDetailed {
  bookings: ReportDetailedBooking[];
  total: number;
  totalPages: number;
  currentPage: number;
}

export interface ReportByRoom {
  _id: string;
  totalBookings: number;
  totalAmount: number;
  totalPaid: number;
  totalRemaining: number;
  avgAmount: number;
  roomTypeName?: string;
  roomNumber?: string | null;
}

// ==================== DASHBOARD INTERFACES ====================

export interface HotelDashboardData {
  rooms: { total: number; available: number; occupied: number; maintenance: number };
  bookings: {
    total: number;
    pending: number;
    checkedIn: number;
    checkedOut: number;
    cancelled: number;
    todayCheckIns: number;
    todayCheckOuts: number;
    bookingsToday: number;
  };
  revenue: {
    today: { total: number; paid: number; bookings: number };
    thisMonth: { total: number; paid: number; remaining: number; bookings: number };
    byDay: Array<{ date: string; total: number; paid: number }>;
    byPaymentMethod: Record<string, number>;
  };
  topRooms: Array<{ roomId: string; roomNumber: string; bookingsCount: number; totalRevenue: number }>;
  recentBookings: Booking[];
}

// ==================== HELPERS ====================

export function getDisplayAmount(b: Booking): number {
  return b.finalAmount ?? b.calculatedAmount ?? 0;
}

export function getPaymentStatus(b: Booking): 'paid' | 'partial' | 'unpaid' | 'no-amount' {
  const d = getDisplayAmount(b);
  if (d <= 0) return 'no-amount';
  if ((b.paidAmount || 0) >= d) return 'paid';
  if ((b.paidAmount || 0) > 0) return 'partial';
  return 'unpaid';
}

function normalizeStatus(status: string): string {
  return status?.replace(/_/g, '-') || status;
}

// ==================== API FUNCTIONS ====================

// --- Rooms ---
export const roomApi = {
  getAll: async (params?: { status?: string; roomTypeId?: string; floor?: number }): Promise<Room[]> => {
    const q = new URLSearchParams();
    if (params?.status) q.append('status', params.status);
    if (params?.roomTypeId) q.append('roomTypeId', params.roomTypeId);
    if (params?.floor) q.append('floor', String(params.floor));
    q.append('limit', '200');
    const qs = q.toString();
    const result = await fetchWithAuth(`${BASE_URL}/rooms${qs ? `?${qs}` : ''}`);
    return result.rooms || result.data || result;
  },
  getById: async (id: string): Promise<Room> => {
    const result = await fetchWithAuth(`${BASE_URL}/rooms/${id}`);
    return result.room || result.data || result;
  },
  create: async (data: Partial<Room>): Promise<Room> => {
    const result = await fetchWithAuth(`${BASE_URL}/rooms`, { method: 'POST', body: JSON.stringify(data) });
    return result.room || result.data || result;
  },
  update: async (id: string, data: Partial<Room>): Promise<Room> => {
    const result = await fetchWithAuth(`${BASE_URL}/rooms/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return result.room || result.data || result;
  },
  updateStatus: async (id: string, status: string): Promise<Room> => {
    const result = await fetchWithAuth(`${BASE_URL}/rooms/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
    return result.room || result.data || result;
  },
  delete: async (id: string): Promise<void> => {
    await fetchWithAuth(`${BASE_URL}/rooms/${id}`, { method: 'DELETE' });
  },
};

// --- Room Types ---
export const roomTypeApi = {
  getAll: async (): Promise<RoomType[]> => {
    const result = await fetchWithAuth(`${BASE_URL}/room-types?limit=200`);
    return result.roomTypes || result.data || result;
  },
  getById: async (id: string): Promise<RoomType> => {
    const result = await fetchWithAuth(`${BASE_URL}/room-types/${id}`);
    return result.roomType || result.data || result;
  },
  create: async (data: Partial<RoomType>): Promise<RoomType> => {
    const result = await fetchWithAuth(`${BASE_URL}/room-types`, { method: 'POST', body: JSON.stringify(data) });
    return result.roomType || result.data || result;
  },
  update: async (id: string, data: Partial<RoomType>): Promise<RoomType> => {
    const result = await fetchWithAuth(`${BASE_URL}/room-types/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return result.roomType || result.data || result;
  },
  delete: async (id: string): Promise<void> => {
    await fetchWithAuth(`${BASE_URL}/room-types/${id}`, { method: 'DELETE' });
  },
};

// --- Bookings ---
export const bookingApi = {
  getAll: async (params?: { status?: string; roomId?: string; clientName?: string; startDate?: string; endDate?: string }): Promise<Booking[]> => {
    const q = new URLSearchParams();
    if (params?.status) q.append('status', params.status);
    if (params?.roomId) q.append('roomId', params.roomId);
    if (params?.clientName) q.append('clientName', params.clientName);
    if (params?.startDate) q.append('startDate', params.startDate);
    if (params?.endDate) q.append('endDate', params.endDate);
    q.append('limit', '200');
    const qs = q.toString();
    const result = await fetchWithAuth(`${BASE_URL}/bookings${qs ? `?${qs}` : ''}`);
    const arr = result.bookings || result.data || result;
    if (Array.isArray(arr)) return arr.map((b: any) => ({ ...b, status: normalizeStatus(b.status) }));
    return arr;
  },
  getById: async (id: string): Promise<Booking> => {
    const result = await fetchWithAuth(`${BASE_URL}/bookings/${id}`);
    const b = result.booking || result.data || result;
    return { ...b, status: normalizeStatus(b.status) };
  },
  create: async (data: CreateBookingDto): Promise<Booking> => {
    const result = await fetchWithAuth(`${BASE_URL}/bookings`, { method: 'POST', body: JSON.stringify(data) });
    return result.booking || result.data || result;
  },
  update: async (id: string, data: Partial<CreateBookingDto>): Promise<Booking> => {
    const result = await fetchWithAuth(`${BASE_URL}/bookings/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return result.booking || result.data || result;
  },
  checkIn: async (id: string): Promise<Booking> => {
    const result = await fetchWithAuth(`${BASE_URL}/bookings/${id}/check-in`, { method: 'POST' });
    return result.booking || result.data || result;
  },
  checkOut: async (id: string, checkOut?: string): Promise<Booking> => {
    const result = await fetchWithAuth(`${BASE_URL}/bookings/${id}/check-out`, {
      method: 'POST',
      body: checkOut ? JSON.stringify({ checkOut }) : '{}',
    });
    return result.booking || result.data || result;
  },
  cancel: async (id: string): Promise<Booking> => {
    const result = await fetchWithAuth(`${BASE_URL}/bookings/${id}/cancel`, { method: 'POST' });
    return result.booking || result.data || result;
  },
  pay: async (id: string, amount: number, paymentMethod: string): Promise<any> => {
    const result = await fetchWithAuth(`${BASE_URL}/bookings/${id}/pay`, {
      method: 'POST',
      body: JSON.stringify({ amount, paymentMethod }),
    });
    return result;
  },
};

// --- Invoices ---
export const invoiceApi = {
  getAll: async (params?: { status?: string }): Promise<HotelInvoice[]> => {
    const q = new URLSearchParams();
    if (params?.status) q.append('status', params.status);
    q.append('limit', '200');
    const qs = q.toString();
    const result = await fetchWithAuth(`${BASE_URL}/invoices${qs ? `?${qs}` : ''}`);
    return result.invoices || result.data || result;
  },
  getById: async (id: string): Promise<HotelInvoice> => {
    const result = await fetchWithAuth(`${BASE_URL}/invoices/${id}`);
    return result.invoice || result.data || result;
  },
  getByNumber: async (num: string): Promise<HotelInvoice> => {
    const result = await fetchWithAuth(`${BASE_URL}/invoices/number/${num}`);
    return result.invoice || result.data || result;
  },
  create: async (bookingId: string): Promise<HotelInvoice> => {
    const result = await fetchWithAuth(`${BASE_URL}/invoices`, { method: 'POST', body: JSON.stringify({ bookingId }) });
    return result.invoice || result.data || result;
  },
  updateStatus: async (id: string, status: string): Promise<HotelInvoice> => {
    const result = await fetchWithAuth(`${BASE_URL}/invoices/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
    return result.invoice || result.data || result;
  },
  getPdf: async (id: string): Promise<Blob> => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${BASE_URL}/invoices/${id}/pdf`, {
      headers: { ...(token && token !== 'undefined' ? { Authorization: `Bearer ${token.replace(/"/g, '')}` } : {}) },
    });
    if (!res.ok) throw new Error('Failed to get PDF');
    return res.blob();
  },
};

// --- Reports ---
export const bookingReportApi = {
  getSummary: async (params: { startDate: string; endDate: string; status?: string }): Promise<ReportSummary> => {
    const q = new URLSearchParams({ startDate: params.startDate, endDate: params.endDate });
    if (params.status) q.append('status', params.status);
    const result = await fetchWithAuth(`${BASE_URL}/bookings/reports/summary?${q.toString()}`);
    return result.summary || result.data || result;
  },
  getDetailed: async (params: { startDate: string; endDate: string; status?: string; roomId?: string; page?: number; limit?: number }): Promise<ReportDetailed> => {
    const q = new URLSearchParams({ startDate: params.startDate, endDate: params.endDate });
    if (params.status) q.append('status', params.status);
    if (params.roomId) q.append('roomId', params.roomId);
    if (params.page) q.append('page', String(params.page));
    if (params.limit) q.append('limit', String(params.limit));
    const result = await fetchWithAuth(`${BASE_URL}/bookings/reports/detailed?${q.toString()}`);
    return result.detailed || result.data || result;
  },
  getByRoom: async (params: { startDate: string; endDate: string }): Promise<ReportByRoom[]> => {
    const q = new URLSearchParams({ startDate: params.startDate, endDate: params.endDate });
    const result = await fetchWithAuth(`${BASE_URL}/bookings/reports/by-room?${q.toString()}`);
    return result.reportByRoom || result.data || result;
  },
};

// --- Hotel Dashboard ---
export const hotelDashboardApi = {
  getStats: async (): Promise<HotelDashboardData> => {
    const result = await fetchWithAuth(`${BASE_URL}/dashboard/hotel`);
    return result.data || result;
  },
};